import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
	ExternalLink,
	Loader2,
	MoreHorizontal,
	Printer,
	RefreshCw,
	TriangleAlert,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/features/auth/use-auth";
import { AddNetworkPrinterDialog } from "@/features/drukarki/add-network-printer-dialog";
import { FixPrinterDialog, type FixTarget } from "@/features/drukarki/fix-printer-dialog";
import { DisabledReason } from "@/features/drukarki/map-menus";
import { OperationDialog } from "@/features/drukarki/operation-dialog";
import {
	connectionRemovable,
	deviceActionable,
	operationKindLabel,
	pluralPl,
	printerFixable,
	printerReinstallable,
	printerRemovable,
} from "@/features/drukarki/printer-labels";
import {
	assessPlanItem,
	destructiveConsequence,
	isDestructive,
	type PlanAction,
	type PlanNames,
	planItemText,
	planOperationKind,
	startPlanAction,
} from "@/features/drukarki/printer-plan";
import {
	assignBlocker,
	buildPharmacyMap,
	type Installation,
	type PrinterNode,
	type PrinterNodeKind,
	printerProblems,
	type StationNode,
} from "@/features/drukarki/printer-topology";
import type {
	NetworkInfo,
	OperationKind,
	PharmacyPrintersResponse,
	PrinterDevice,
	PrinterOperation,
	PrinterPlace,
} from "@/features/drukarki/printers-api";
import { RemoveDialog, type RemoveTarget } from "@/features/drukarki/remove-dialog";
import { ShareDialog, type ShareTarget } from "@/features/drukarki/share-dialog";
import {
	printerErrorMessage,
	usePharmacyPrintersQuery,
	usePrinterOperation,
	useRefreshPrintersMutation,
} from "@/features/drukarki/use-printers-query";
import { Visible } from "@/features/visibility/visible";
import { badgeBase, statusClasses } from "@/lib/colors";
import { cn } from "@/lib/utils";

/**
 * Drukarki apteki w prawym panelu czatu: jedna linia na fizyczną drukarkę
 * (grupowanie i problemy z mapy modułu Drukarki) i menu z akcjami. W przeciwieństwie
 * do modułu akcje nie trafiają do planu — startują od razu (destrukcyjne po
 * potwierdzeniu) tymi samymi endpointami, z tymi samymi blokadami co pozycja planu.
 */
export function PharmacyPrinters({ idfirm }: { idfirm: number }) {
	// Każdy endpoint drukarek wymaga tego elementu — bez niego sekcja dawałaby same 403.
	return (
		<Visible id="sidebar.konfiguracja.drukarki">
			<PrintersSection idfirm={idfirm} />
		</Visible>
	);
}

type Pending = { nodeId: string; action: PlanAction; names: PlanNames };

type DialogState =
	| { kind: "confirm"; pending: Pending }
	| { kind: "share"; nodeId: string; target: ShareTarget }
	| { kind: "add"; nodeId: string; device: PrinterDevice; host: string }
	| { kind: "remove"; nodeId: string; target: RemoveTarget }
	| { kind: "fix"; nodeId: string; target: FixTarget };

type Ctx = {
	view: PharmacyPrintersResponse;
	stations: StationNode[];
	idfirm: number;
	/** Trwa operacja w aptece — backend przyjmuje jedną naraz. */
	lock: string | null;
	op: {
		nodeId: string;
		operationId: number;
		kind: OperationKind;
		operation: PrinterOperation | null;
	} | null;
	/** Akcja bez wyboru: od razu, a destrukcyjna po potwierdzeniu. */
	request: (pending: Pending) => void;
	open: (dialog: DialogState) => void;
	showDetails: (operationId: number) => void;
};

/** Sieci komputerów panel nie pobiera — zasięg sprawdza wysyłka (jak plan bez znanej sieci). */
const NO_NETWORKS = new Map<string, NetworkInfo | null>();
const LOCK = "Trwa operacja na drukarkach tej apteki — kolejna akcja po jej zakończeniu.";
const KIND_WORD: Record<PrinterNodeKind, string> = {
	cable: "kabel",
	network: "sieć",
	wsd: "WSD",
	manual: "połączenie",
	other: "kolejka",
};
const CHIP = "h-6 rounded border border-border px-1.5 py-0 text-xs";

const star = (i: Installation) => `${i.device.displayName}${i.isDefault ? "★" : ""}`;

/** „kabel ST1★ → ST2★”, „sieć · ST1, ST3★” (★ = domyślna na tym stanowisku). */
function describe(node: PrinterNode): string {
	if (node.kind === "cable") {
		const host = node.installations.find((i) => i.link === "cable");
		const rest = node.installations.filter((i) => i !== host);
		return `kabel ${host ? star(host) : ""}${rest.length ? ` → ${rest.map(star).join(", ")}` : ""}`;
	}
	return `${KIND_WORD[node.kind]} · ${node.installations.map(star).join(", ")}`;
}

/** Kolejka, o której mówi linia: wstrzymana/wyłączona przed tą z samymi zadaniami (tylko ją „Wznów” zmienia). */
function problemQueue(node: PrinterNode): Installation | undefined {
	const bad = node.installations.filter((i) => i.printer && printerProblems(i.printer).length > 0);
	return bad.find((i) => i.printer?.paused || i.printer?.offline) ?? bad[0];
}

/** „ST2 · wstrzymana · 4 zad.”; problem bez kolejki (np. zmieniony adres) — tekst modułu. */
function problemText(node: PrinterNode): string | null {
	if (node.problems.length === 0) return null;
	const queue = problemQueue(node);
	const p = queue?.printer;
	if (!queue || !p) return node.problems[0];
	return [
		queue.device.displayName,
		p.paused ? "wstrzymana" : null,
		p.offline ? "wyłączona" : null,
		p.jobs?.error ? "błąd" : null,
		p.jobs?.count ? `${p.jobs.count} zad.` : null,
	]
		.filter(Boolean)
		.join(" · ");
}

/** Ta sama ocena co pozycja planu modułu; `null` = można wysłać. */
function blockerOf(action: PlanAction, ctx: Ctx): string | null {
	if (ctx.lock) return ctx.lock;
	const assessment = assessPlanItem(
		{
			id: "helpdesk",
			action,
			names: {},
			addedAt: "",
			status: "draft",
			operationId: null,
			error: null,
			startedAt: null,
			finishedAt: null,
		},
		{ view: ctx.view, sharing: true, earlier: [] },
	);
	return assessment.state === "blocked" ? assessment.reason : null;
}

/** „Wznów / wyczyść kolejkę” — to, co okno naprawy modułu zaznacza na start; `null` = nie ma czego. */
function fixAction(i: Installation): PlanAction | null {
	const p = i.printer;
	if (!p || !printerFixable(p)) return null;
	const resume = p.offline || p.paused;
	const purge = p.jobs != null && (p.jobs.error || p.jobs.count > 0);
	if (!resume && !purge) return null;
	return {
		kind: "fix_printer",
		input: { deviceUuid: i.device.deviceUuid, printerName: p.name, purge, resume },
	};
}

/** Powody wyszarzenia chipów: wspólny raz, pozostałe z nazwą stanowiska. */
function reasonLines(
	items: { name: string; reason: string | null }[],
	shared: string | null,
): string[] {
	if (shared) return [shared];
	const lines = items.flatMap(({ name, reason }) => {
		if (!reason) return [];
		return [
			reason.includes(name)
				? reason
				: `${name}: ${reason.charAt(0).toLowerCase()}${reason.slice(1)}`,
		];
	});
	return [...new Set(lines)];
}

function PrintersSection({ idfirm }: { idfirm: number }) {
	const { user } = useAuth();
	const storageKey = `helpdesk.chat.printersCollapsed:${user?.tenantId}:${user?.id}`;
	const [collapsed, setCollapsed] = useState(() => localStorage.getItem(storageKey) === "1");
	const place: PrinterPlace = { kind: "pharmacy", idfirm };
	// Zwinięta: bez zapytania, ale z danymi z pamięci podręcznej (chip problemu zostaje).
	const query = usePharmacyPrintersQuery(place, !collapsed);
	const refresh = useRefreshPrintersMutation();
	const data = query.data;
	const map = useMemo(
		() => (data ? buildPharmacyMap({ view: data, sharing: true, networks: NO_NETWORKS }) : null),
		[data],
	);
	const [active, setActive] = useState<{
		nodeId: string;
		operationId: number;
		kind: OperationKind;
	} | null>(null);
	const own = usePrinterOperation(active?.operationId ?? null);
	const [dialog, setDialog] = useState<DialogState | null>(null);
	const [details, setDetails] = useState<number | null>(null);
	const start = useMutation({
		mutationFn: (pending: Pending) => startPlanAction(pending.action),
		onSuccess: ({ operationId }, pending) =>
			setActive({ nodeId: pending.nodeId, operationId, kind: planOperationKind(pending.action) }),
		onError: (error) => toast.error(`Nie udało się uruchomić: ${printerErrorMessage(error)}`),
	});

	const toggle = () => {
		const next = !collapsed;
		setCollapsed(next);
		if (next) localStorage.setItem(storageKey, "1");
		else localStorage.removeItem(storageKey);
	};
	const ownRunning =
		active != null && !own.isError && (own.operation?.status ?? "running") === "running";
	const foreign =
		data?.runningOperation && data.runningOperation.operationId !== active?.operationId
			? data.runningOperation
			: null;
	const problemCount = map?.printers.filter((p) => p.problems.length > 0).length ?? 0;
	const dialogNode = dialog && dialog.kind !== "confirm" ? dialog.nodeId : null;
	const fromDialog = (action: PlanAction, names: PlanNames) => {
		if (dialogNode) start.mutate({ nodeId: dialogNode, action, names });
	};
	const ctx: Ctx | null =
		data && map
			? {
					view: data,
					stations: map.stations,
					idfirm,
					lock: start.isPending || ownRunning || data.runningOperation ? LOCK : null,
					op: active ? { ...active, operation: own.operation } : null,
					request: (pending) =>
						isDestructive(pending.action)
							? setDialog({ kind: "confirm", pending })
							: start.mutate(pending),
					open: setDialog,
					showDetails: setDetails,
				}
			: null;

	return (
		<section className="space-y-1.5 border-t border-border px-3 pt-3">
			<h3 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
				<button
					type="button"
					className="flex items-center gap-1.5 hover:text-foreground"
					aria-expanded={!collapsed}
					onClick={toggle}
				>
					<Printer className="size-3.5" aria-hidden />
					Drukarki{map ? <span className="tabular-nums"> {map.printers.length}</span> : null}
				</button>
				{problemCount > 0 ? (
					<span className={cn(badgeBase, statusClasses.warning, "px-1 py-0")}>
						{`${problemCount} ${pluralPl(problemCount, "problem", "problemy", "problemów")}`}
					</span>
				) : null}
				<span className="ml-auto flex items-center">
					<Button
						size="icon"
						variant="ghost"
						className="size-6"
						aria-label="Odśwież listę drukarek"
						title="Poproś stanowiska o aktualną listę drukarek"
						disabled={refresh.isPending}
						onClick={() => refresh.mutate(place)}
					>
						<RefreshCw className="size-3" />
					</Button>
					<Button asChild size="icon" variant="ghost" className="size-6">
						<Link
							to="/konfiguracja/drukarki"
							search={{ apteka: idfirm, tab: "drukarki" }}
							aria-label="Drukarki tej apteki w module Drukarki"
							title="Moduł Drukarki"
						>
							<ExternalLink className="size-3" />
						</Link>
					</Button>
				</span>
			</h3>
			{collapsed ? null : query.isLoading ? (
				<p className="text-xs text-muted-foreground">Wczytywanie drukarek…</p>
			) : !ctx || !map ? (
				<p className="text-xs text-status-error-text">
					Nie udało się pobrać drukarek: {printerErrorMessage(query.error)}
				</p>
			) : (
				<>
					{foreign ? (
						<p className="flex h-6 items-center gap-1.5 text-xs text-muted-foreground">
							<Loader2 className="size-3 shrink-0 animate-spin" aria-hidden />
							<span className="min-w-0 flex-1 truncate">
								Trwa operacja: {operationKindLabel(foreign.kind)}
								{foreign.printerName ? ` — ${foreign.printerName}` : ""}
							</span>
							<button
								type="button"
								className="shrink-0 underline"
								onClick={() => setDetails(foreign.operationId)}
							>
								szczegóły
							</button>
						</p>
					) : null}
					<ul>
						{map.printers.map((node) => (
							<PrinterLine key={node.id} node={node} ctx={ctx} />
						))}
					</ul>
					<ConfirmDialog
						open={dialog?.kind === "confirm"}
						onOpenChange={(open) => (open ? undefined : setDialog(null))}
						title={dialog?.kind === "confirm" ? planItemText(dialog.pending).title : ""}
						description={
							dialog?.kind === "confirm" ? destructiveConsequence(dialog.pending, data) : undefined
						}
						variant="destructive"
						confirmLabel="Wykonaj"
						onConfirm={() => {
							if (dialog?.kind === "confirm") start.mutate(dialog.pending);
							setDialog(null);
						}}
					/>
					<ShareDialog
						immediate
						target={dialog?.kind === "share" ? dialog.target : null}
						devices={ctx.view.devices}
						minAgentVersion={ctx.view.minAgentVersion}
						onPlan={fromDialog}
						onClose={() => setDialog(null)}
					/>
					<AddNetworkPrinterDialog
						immediate
						device={dialog?.kind === "add" ? dialog.device : null}
						initialHost={dialog?.kind === "add" ? dialog.host : undefined}
						onPlan={fromDialog}
						onClose={() => setDialog(null)}
					/>
					<RemoveDialog
						immediate
						target={dialog?.kind === "remove" ? dialog.target : null}
						onPlan={fromDialog}
						onClose={() => setDialog(null)}
					/>
					<FixPrinterDialog
						immediate
						target={dialog?.kind === "fix" ? dialog.target : null}
						onPlan={fromDialog}
						onClose={() => setDialog(null)}
					/>
				</>
			)}
			<OperationDialog operationId={details} onClose={() => setDetails(null)} />
		</section>
	);
}

function PrinterLine({ node, ctx }: { node: PrinterNode; ctx: Ctx }) {
	const desc = describe(node);
	const problem = problemText(node);
	const queue = problemQueue(node);
	const resume: Pending | null =
		queue?.printer &&
		printerFixable(queue.printer) &&
		(queue.printer.paused || queue.printer.offline)
			? {
					nodeId: node.id,
					action: {
						kind: "fix_printer",
						input: {
							deviceUuid: queue.device.deviceUuid,
							printerName: queue.printer.name,
							purge: false,
							resume: true,
						},
					},
					names: { device: queue.device.displayName, printer: queue.printer.name },
				}
			: null;
	const resumeBlock = resume ? blockerOf(resume.action, ctx) : null;
	const op = ctx.op?.nodeId === node.id ? ctx.op : null;
	const opStatus = op ? (op.operation?.status ?? "running") : null;
	return (
		<li aria-label={node.name} className="flex h-6 items-center gap-1.5 text-xs">
			{problem ? (
				<TriangleAlert className="size-3.5 shrink-0 text-status-warning-text" aria-hidden />
			) : (
				<Printer className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
			)}
			<span className="min-w-0 flex-1 truncate" title={desc}>
				{node.name}{" "}
				{problem ? (
					<span className="text-status-warning-text" title={node.problems.join("; ")}>
						{problem}
					</span>
				) : (
					<span className="text-muted-foreground">{desc}</span>
				)}
			</span>
			{op ? (
				<span
					className={cn(
						"flex shrink-0 items-center gap-1",
						opStatus === "failed"
							? "text-status-error-text"
							: opStatus === "succeeded"
								? "text-status-success-text"
								: "text-muted-foreground",
					)}
				>
					{opStatus === "running" ? <Loader2 className="size-3 animate-spin" aria-hidden /> : null}
					{opStatus === "failed"
						? "błąd"
						: opStatus === "succeeded"
							? "wykonano"
							: `${operationKindLabel(op.kind)}…`}
					{opStatus === "succeeded" ? null : (
						<button
							type="button"
							className="underline"
							onClick={() => ctx.showDetails(op.operationId)}
						>
							szczegóły
						</button>
					)}
				</span>
			) : resume ? (
				// Powód jako dymek na otoczce: wyłączony przycisk nie pokazuje własnego `title`.
				<span className="shrink-0" title={resumeBlock ?? undefined}>
					<Button
						size="sm"
						variant="outline"
						className="h-5 px-1.5 text-xs"
						disabled={resumeBlock != null}
						onClick={() => ctx.request(resume)}
					>
						Wznów
					</Button>
				</span>
			) : null}
			<PrinterMenu node={node} ctx={ctx} />
		</li>
	);
}

function Action({
	label,
	block,
	onSelect,
	destructive,
}: {
	label: string;
	block: string | null;
	onSelect: () => void;
	destructive?: boolean;
}) {
	if (block) return <DisabledReason label={label} reason={block} />;
	return (
		<DropdownMenuItem variant={destructive ? "destructive" : "default"} onSelect={onSelect}>
			{label}
		</DropdownMenuItem>
	);
}

function Reasons({ lines }: { lines: string[] }) {
	return lines.map((line) => (
		<p key={line} className="px-2 pb-1 text-[11px] text-muted-foreground">
			{line}
		</p>
	));
}

/** „Odłącz od STx…” instalacji: połączenie Farmastera, ręczne połączenie albo kolejka (jak w module). */
function detachAction(
	node: PrinterNode,
	i: Installation,
	ctx: Ctx,
): { label: string; block: string | null; onSelect: () => void } | null {
	const { device, printer, connection } = i;
	const name = device.displayName;
	// Komputera z drukarką na kablu się nie „odłącza”.
	if (i.link === "cable") return null;
	if (connection?.managed && connection.connectionId != null) {
		const forget = !deviceActionable(device);
		const action: PlanAction = {
			kind: "disconnect",
			connectionId: connection.connectionId,
			forget,
		};
		const names = {
			client: name,
			host: connection.hostDisplayName ?? undefined,
			printer: connection.printerName ?? connection.path,
		};
		return {
			label: forget ? `Zapomnij połączenie ${name}…` : `Odłącz od ${name}…`,
			block: blockerOf(action, ctx),
			onSelect: () => ctx.request({ nodeId: node.id, action, names }),
		};
	}
	// Ręczne połączenie i kolejka: okno usuwania modułu (wybór nowej domyślnej) jest potwierdzeniem.
	if (connection && connectionRemovable(connection)) {
		return {
			label: `Odłącz od ${name}…`,
			block: blockerOf(
				{
					kind: "remove_connection",
					input: {
						deviceUuid: device.deviceUuid,
						path: connection.path,
						userName: connection.userName,
						newDefault: null,
					},
				},
				ctx,
			),
			onSelect: () =>
				ctx.open({
					kind: "remove",
					nodeId: node.id,
					target: { kind: "connection", device, connection },
				}),
		};
	}
	if (printer && printerRemovable(printer)) {
		return {
			label: `Odłącz od ${name}…`,
			block: blockerOf(
				{
					kind: "remove_printer",
					input: { deviceUuid: device.deviceUuid, printerName: printer.name, newDefault: null },
				},
				ctx,
			),
			onSelect: () =>
				ctx.open({ kind: "remove", nodeId: node.id, target: { kind: "printer", device, printer } }),
		};
	}
	return null;
}

function PrinterMenu({ node, ctx }: { node: PrinterNode; ctx: Ctx }) {
	const has = new Set(node.installations.map((i) => i.device.deviceUuid));
	const cablePrinter = node.installations.find((i) => i.link === "cable")?.printer ?? null;
	const assignable = (node.kind === "cable" && cablePrinter != null) || node.kind === "network";
	const sharedBlock = ctx.lock ?? node.assignBlock;
	const assign = assignable
		? ctx.stations
				.filter((s) => !has.has(s.id))
				.map((station) => ({
					station,
					name: station.device.displayName,
					reason:
						sharedBlock ?? assignBlocker(node, station, NO_NETWORKS, ctx.view.minAgentVersion),
				}))
		: [];
	const defaults = node.installations.map((i) => {
		const printerName = i.printer?.name ?? i.connection?.path ?? "";
		const action: PlanAction = {
			kind: "set_default",
			input: { deviceUuid: i.device.deviceUuid, printerName },
		};
		return {
			i,
			action,
			name: i.device.displayName,
			label: i.printer?.name ?? i.connection?.printerName ?? printerName,
			reason: i.isDefault ? null : blockerOf(action, ctx),
		};
	});
	const queues = node.installations.filter((i) => i.printer && printerFixable(i.printer));
	const fixes = queues.flatMap((i) => {
		const action = fixAction(i);
		return action ? [{ i, action }] : [];
	});
	const spoolers = [...new Map(queues.map((i) => [i.device.deviceUuid, i.device])).values()];
	const detach = node.installations.flatMap((i) => {
		const item = detachAction(node, i, ctx);
		return item
			? [{ key: `${i.device.deviceUuid}:${i.printer?.name ?? i.connection?.path}`, ...item }]
			: [];
	});
	const reinstall = queues.filter((i) => i.printer && printerReinstallable(i.printer));

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					size="icon"
					variant="ghost"
					className="size-6 shrink-0 text-muted-foreground hover:text-foreground"
					aria-label={`Akcje drukarki ${node.name}`}
				>
					<MoreHorizontal className="size-3.5" aria-hidden />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-72">
				<DropdownMenuLabel className="space-y-0.5">
					<span className="block truncate">{node.name}</span>
					<span className="block truncate text-[11px] font-normal text-muted-foreground">
						{node.kindLabel}
					</span>
				</DropdownMenuLabel>
				{assign.length > 0 ? (
					<>
						<DropdownMenuLabel className="pb-0 text-[11px] font-normal text-muted-foreground">
							{node.kind === "cable" ? "Udostępnij na" : "Dodaj na stanowisko"}
						</DropdownMenuLabel>
						<div className="flex flex-wrap gap-1 px-2 py-1">
							{assign.map(({ station, name, reason }) => (
								<DropdownMenuItem
									key={station.id}
									className={CHIP}
									disabled={reason != null}
									onSelect={() => {
										if (node.kind === "cable" && node.host && cablePrinter) {
											ctx.open({
												kind: "share",
												nodeId: node.id,
												target: {
													host: node.host,
													printer: cablePrinter,
													clientDeviceUuid: station.id,
												},
											});
										} else if (node.address) {
											ctx.open({
												kind: "add",
												nodeId: node.id,
												device: station.device,
												host: node.address,
											});
										}
									}}
								>
									{name}
								</DropdownMenuItem>
							))}
						</div>
						<Reasons lines={reasonLines(assign, sharedBlock)} />
					</>
				) : null}
				<DropdownMenuLabel className="pb-0 text-[11px] font-normal text-muted-foreground">
					Domyślna na
				</DropdownMenuLabel>
				<div className="flex flex-wrap gap-1 px-2 py-1">
					{defaults.map(({ i, action, name, label, reason }) => (
						<DropdownMenuCheckboxItem
							key={`${i.device.deviceUuid}:${action.kind === "set_default" ? action.input.printerName : ""}:${i.connection?.userName ?? ""}`}
							className={cn(CHIP, "pl-6")}
							checked={i.isDefault}
							disabled={reason != null}
							onSelect={() => {
								if (!i.isDefault) {
									ctx.request({ nodeId: node.id, action, names: { device: name, printer: label } });
								}
							}}
						>
							{name}
						</DropdownMenuCheckboxItem>
					))}
				</div>
				<Reasons lines={reasonLines(defaults, ctx.lock)} />
				<DropdownMenuSeparator />
				{queues.length === 0 ? null : fixes.length === 0 ? (
					<DropdownMenuItem disabled>Wznów / wyczyść kolejkę (pusta)</DropdownMenuItem>
				) : (
					fixes.map(({ i, action }) => (
						<Action
							key={i.device.deviceUuid}
							label={`Wznów / wyczyść kolejkę na ${i.device.displayName}`}
							block={blockerOf(action, ctx)}
							onSelect={() =>
								ctx.request({
									nodeId: node.id,
									action,
									names: { device: i.device.displayName, printer: i.printer?.name },
								})
							}
						/>
					))
				)}
				{spoolers.map((device) => {
					const action: PlanAction = { kind: "reset_spooler", deviceUuid: device.deviceUuid };
					return (
						<Action
							key={device.deviceUuid}
							label={`Reset bufora wydruku na ${device.displayName}…`}
							block={blockerOf(action, ctx)}
							destructive
							onSelect={() =>
								ctx.request({ nodeId: node.id, action, names: { device: device.displayName } })
							}
						/>
					);
				})}
				{detach.map(({ key, label, block, onSelect }) => (
					<Action key={key} label={label} block={block} destructive onSelect={onSelect} />
				))}
				{reinstall.map((i) =>
					i.printer ? (
						<Action
							key={i.device.deviceUuid}
							label={`Zainstaluj ponownie na ${i.device.displayName}…`}
							block={blockerOf(
								{
									kind: "reinstall_printer",
									input: {
										deviceUuid: i.device.deviceUuid,
										printerName: i.printer.name,
										driverName: "",
									},
								},
								ctx,
							)}
							onSelect={() =>
								i.printer &&
								ctx.open({
									kind: "fix",
									nodeId: node.id,
									target: { device: i.device, printer: i.printer },
								})
							}
						/>
					) : null,
				)}
				<DropdownMenuSeparator />
				<DropdownMenuItem asChild>
					<Link to="/konfiguracja/drukarki" search={{ apteka: ctx.idfirm, tab: "drukarki" }}>
						<ExternalLink className="size-3.5" aria-hidden />
						Szczegóły w Drukarkach
					</Link>
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
